import { KeyValue } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { TooltipDirective } from '@cloudfactorydk/ng2-tooltip-directive';
import { cloneDeep } from "lodash-es";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Finishing, PageSize, Settings } from 'src/app/shared-lib/core/settings';
import { HardCover, RingColor } from 'src/app/shared-lib/domains/product';
import { AppService } from 'src/app/shared/app.service';
import { CartService } from "../../shared/cart.service";
import { UtilsService } from "../../shared/services/utils.service";

// TODO: Este tipo lo añado para evitar un error de compilación en producción pero quizá habría que revisarlo porque no me gusta mucho.
type BindingOptions = Array<{ value: RingColor | HardCover; }>;

@Component({
  selector: 'app-binding-options',
  templateUrl: './binding-options.component.html',
  styleUrls: ['./binding-options.component.scss'],
  standalone: false
})
export class BindingOptionsComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChildren('tooltip1') tooltip1: QueryList<TooltipDirective>;
  @ViewChildren('tooltip2') tooltip2: QueryList<TooltipDirective>;

  @Input() additionalClasses: { [key: string]: boolean; } = {};
  @Input() settings;
  @Input() qty;
  @Input() printProduct;
  @Input() file = null;
  @Input() isModal = false;
  @Output() optionSelect = new EventEmitter<any>();
  options = ['laminationOptions', 'ringColor', 'hardCoverFront', 'hardCoverBack'];
  optionSelectd = 0;
  settingOption;
  bindingStock;
  printingGroup;
  groupedOptions: { noCategory: BindingOptions; groupedByCategory: { [category: string]: BindingOptions; }; } = { noCategory: [], groupedByCategory: {} };
  isLaminatedCover = false;
  isBindingLaminated = false;
  isA3 = false;
  Settings: Settings;
  optionMaps = {};

  private destroy$ = new Subject<void>();

  constructor(
    private cartService: CartService,
    public utils: UtilsService,
    public appService: AppService) {
  }

  ngOnInit() {
    this.isLaminatedCover = this.printProduct?.product.printingGroup.printSettings?.[Settings.COVER_LAMINATED];
    this.isBindingLaminated = this.printProduct?.product.printingGroup.printSettings?.[Settings.FINISHING] === Finishing.BINDING_LAMINATED;
    this.isA3 = this.printProduct?.product.printingGroup.printSettings?.pageSize === PageSize.A3;
    this.optionSelectd = this.isLaminatedCover ? 0 : 1;
    this.settingOption = this.options[this.optionSelectd];

    this.setMaps();
    this.cartService.activePrintItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe(printProduct => {
        if (!printProduct) return;
        this.printingGroup = printProduct.product.printingGroup;
        this.getGroupedOptions(this.settingOption);
      });
  }

  ngAfterViewChecked() {
    this.tooltip1.forEach(t => {
      this.appService.registerTooltip(t);
    });
    this.tooltip2.forEach(t => {
      this.appService.registerTooltip(t);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.tooltip1.forEach(t => {
      this.appService.unregisterTooltip(t);
    });
    this.tooltip2.forEach(t => {
      this.appService.unregisterTooltip(t);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.optionSelectd = this.isLaminatedCover ? 0 : 1;
    this.settingOption = this.options[this.optionSelectd];
    // Si `settings` cambia (p.ej. cuando llegan tarde las opciones de anillas/tapas desde
    // Firestore y el padre re-clona los settings) reconstruimos mapas y opciones agrupadas
    // para que el grid no se quede vacío hasta que el usuario interactúe.
    if (changes['settings'] && this.settings) {
      this.setMaps();
      this.getGroupedOptions(this.settingOption);
    }
  }

  setMaps() {
    for (let option of this.options) {
      if (option === 'laminationOptions') {
        continue;
      }
      if (!this.optionMaps[option]) {
        this.optionMaps[option] = new Map<string, number>();
      } else {
        this.optionMaps[option].clear();
      }
      const opts = this.settings?.[option]?.options ?? [];
      opts.forEach((_option, index) => {
        this.optionMaps[option].set(_option.value.id, index);
      });
    }
  }

  onSelectOption(i, soldOut = false) {
    if (soldOut) {
      return;
    }
    const prev = this.settings[this.settingOption].selected;
    this.settings[this.settingOption].selected = i + 1;
    this.optionSelect.emit({ setting: this.settingOption, option: this.settings[this.settingOption].selected, prev });
  }

  optionSelected(event) {
    if (!['ringColor', 'hardCoverFront', 'hardCoverBack'].includes(event.setting)) {
      return;
    }
    this.settings[event.setting].selected = event.option;
  }

  onSegmentChange(event) {
    this.settingOption = event.detail.value;
    this.optionSelect.emit({ setting: 'segment' });
    this.getGroupedOptions(this.settingOption);
  }

  isSomeExclusive(options) {
    return options.some(op => op.value.exclusive);
  }

  isSoldOut(option, selected) {
    const _printSettings = this.printingGroup?.printSettings;
    if (!_printSettings) {
      return false;
    }
    const isHardCover = ['hardCoverFront', 'hardCoverBack'].includes(this.settingOption);
    const isRingColor = this.settingOption === 'ringColor';
    const stockArray = isHardCover ? this.cartService.bindingStock?.covers : this.cartService.bindingStock?.rings;
    if (!stockArray) {
      // Si aún no se ha cargado el stock, no marcamos nada como agotado para no
      // bloquear la UI ni provocar errores al pintar.
      return false;
    }
    const cartBindingUses = this.cartService.cartBindingStockUses;
    let itemStock;
    let itemCartUses;
    let qtyToAdd = 1;    //qty to add
    let soldOut = false;
    let availableStock = 0;
    if (_printSettings.grouped) {
      qtyToAdd = selected ? 0 : 1 * this.qty;
    } else {
      if (this.file) {
        qtyToAdd = selected ? 0 : 1 * this.qty;
      } else {
        qtyToAdd = selected ? 0 : this.printingGroup.files.length * this.qty;
      }
    }

    if (_printSettings.grouped) {
      const key = isHardCover ? 'pageSize' : 'diameter';
      const value = isHardCover ? this.printingGroup.printSettings.pageSize : this.printingGroup.binding.ringDiameter;

      itemStock = stockArray.find(el => el.color === option.value.id && el[key] == value);
      availableStock = itemStock?.stock ?? 0;

      if (isHardCover && value === PageSize.A5) {
        availableStock += (stockArray.find(el => el.color === option.value.id && el[key] == PageSize.A4)?.stock ?? 0) * 2;
      }

      if (isRingColor) {
        itemCartUses = cartBindingUses.rings.find(el => el.color === option.value.id && el.diameter == this.printingGroup.binding.ringDiameter)?.uses ?? 0;
      } else if (isHardCover) {
        itemCartUses = cartBindingUses.covers.find(el => el.color === option.value.id && el.pageSize == this.printingGroup.printSettings.pageSize)?.uses ?? 0;
      }
    } else {
      const files = this.file ? [this.file] : this.printingGroup.files;
      for (const file of files) {
        const key = isHardCover ? 'pageSize' : 'diameter';
        const value = isHardCover ? this.printingGroup.printSettings.pageSize : file.binding?.ringDiameter;

        itemStock = stockArray.find(el => el.color === option.value.id && el[key] == value);
        availableStock = itemStock?.stock ?? 0;

        if (isHardCover && value === PageSize.A5) {
          availableStock += (stockArray.find(el => el.color === option.value.id && el[key] == PageSize.A4)?.stock ?? 0) * 2;
        }

        if (isRingColor) {
          itemCartUses = cartBindingUses.rings.find(el => el.color === option.value.id && el.diameter == file.binding?.ringDiameter)?.uses ?? 0;
        } else if (isHardCover) {
          itemCartUses = cartBindingUses.covers.find(el => el.color === option.value.id && el.pageSize == this.printingGroup.printSettings.pageSize)?.uses ?? 0;
        }

        if (!itemStock || (availableStock - itemCartUses - qtyToAdd < 0 && !selected)) {
          soldOut = true;
          break;
        }
      }
    }

    return _printSettings.grouped || this.file
      ? itemStock ? availableStock - itemCartUses - qtyToAdd < 0 && !selected : !selected
      : soldOut;
  }

  someCoverLaminated() {
    const result = this.printProduct.product.printingGroup.files.some(f => f.coverLaminated);
    if (result && this.settingOption === 'hardCoverFront') {
      this.settingOption = 'ringColor';
      this.optionSelect.emit({ setting: 'segment' });
    }
    return result;
  }

  getOptionsId() {
    return this.settingOption === 'ringColor' ? 'rings' : 'covers';
  }

  getGroupedOptions(settingOption) {
    if (settingOption === 'laminationOptions') {
      return;
    }
    if (!this.settings?.[settingOption]?.options) {
      this.groupedOptions = { noCategory: [], groupedByCategory: {} };
      return;
    }

    const noCategory = [];
    const groupedByCategory = {};
    const orderedOptions = cloneDeep(this.settings[settingOption].options).sort((a, b) => a.value.position - b.value.position);
    for (let option of orderedOptions) {
      if (!option.value.category) {
        noCategory.push(option);
      } else {
        if (!groupedByCategory[option.value.category]) {
          groupedByCategory[option.value.category] = [];
        }
        groupedByCategory[option.value.category].push(option);
      }
    }

    this.groupedOptions = { noCategory, groupedByCategory };
  }

  getLaminationOptions(event: any) {
    const indexType = this.settings[Settings.LAMINATION_TYPE].options.findIndex(el => el.value === event.laminationType);
    const indexWeight = this.settings[Settings.LAMINATION_WEIGHT].options.findIndex(el => el.value === event.laminationWeight);
    this.optionSelect.emit({ setting: Settings.LAMINATION_TYPE, option: indexType + 1 });
    this.optionSelect.emit({ setting: Settings.LAMINATION_WEIGHT, option: indexWeight + 1 });
  }

  getCategoryText(category: string) {
    let result = '';
    switch (category) {
      case 'exclusive':
        result = 'Exclusivas Copyfly:';
        break;
      case 'glitter':
        result = 'Anillas con PURPURINA:';
        break;
      case 'fluorescent':
        result = 'Colores flúor:';
        break;
      case 'standard':
        result = 'Colores estándar:';
        break;
      case 'pastel':
        result = 'Colores pastel:';
        break;
      case 'nature':
        result = 'Colores nature:';
        break;
      default:
        result = '';
        break;
    }
    return result;
  }

  getGlobalIndex(id) {
    return this.optionMaps[this.settingOption].get(id);
  }

  originalOrder = (a: KeyValue<string, BindingOptions>, b: KeyValue<string, BindingOptions>): number => {
    return 0;
  };
}
